import React, { useCallback, useEffect, useState } from "react";
import { financeAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDate, todayIso as today, monthStartIso as monthStart } from "../utils/dates";

/**
 * Net revenue: what the shop actually keeps.
 *
 *   revenue − what those items cost − expenses = net
 *
 * The chain is shown as a chain rather than as a single number, because an
 * owner who cannot see where his profit went has no way to act on it.
 */
const percent = (value) => `${Math.round(Number(value || 0) * 100)}%`;


/** "Sep 2026" from the API's "2026-09", in the device's language. */
const monthLabel = (value, locale) => {
  const match = /^(\d{4})-(\d{2})/.exec(String(value));
  if (!match) return value;

  return new Date(Number(match[1]), Number(match[2]) - 1, 1).toLocaleDateString(
    locale,
    { month: "short", year: "numeric" },
  );
};

const Figure = ({ label, value, tone = "neutral", hint }) => {
  const tones = {
    neutral: "text-gray-900",
    good: "text-emerald-700",
    bad: "text-red-600",
    muted: "text-gray-500",
  };

  return (
    <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tones[tone]}`}>{value}</p>
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  );
};

const Revenue = () => {
  const { t, locale } = useI18n();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);
  const [range, setRange] = useState({ start: monthStart(), end: today() });
  const [summary, setSummary] = useState(null);
  const [journal, setJournal] = useState([]);
  const [margins, setMargins] = useState([]);
  const [months, setMonths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);

    try {
      const params = { start_date: range.start, end_date: range.end };
      const [s, j, m, mo] = await Promise.all([
        financeAPI.summary(params),
        financeAPI.journal(params),
        financeAPI.margins(params),
        financeAPI.monthly(6),
      ]);

      setSummary(s.data);
      setJournal(j.data);
      setMargins(m.data);
      setMonths(mo.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("finance.revenue.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.start, range.end]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  const peak = Math.max(...months.map((m) => Math.abs(m.net_revenue)), 1);

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("finance.revenue.title")}
          </h1>
          <p className="text-gray-600">{t("finance.revenue.subtitle")}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="block">
            <span className="text-xs text-gray-500">{t("common.from")}</span>
            <input
              type="date"
              value={range.start}
              onChange={(e) => setRange({ ...range, start: e.target.value })}
              className="block mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">{t("common.to")}</span>
            <input
              type="date"
              value={range.end}
              onChange={(e) => setRange({ ...range, end: e.target.value })}
              className="block mt-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
            />
          </label>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {summary && (
        <>
          {summary.cost_known_ratio < 1 && summary.revenue > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {t("finance.revenue.costWarning", {
                percent: percent(summary.cost_known_ratio),
              })}
            </div>
          )}

          {summary.credit_outstanding > 0 && (
            <div className="rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
              {t("finance.revenue.creditOwed", {
                amount: money(summary.credit_outstanding),
              })}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500">
                {t("finance.revenue.avgDaily")}
              </p>
              {summary.avg_daily_net === null ? (
                <p className="text-sm text-gray-500 mt-1">
                  {t("finance.revenue.avgDailyNone")}
                </p>
              ) : (
                <>
                  <p
                    data-testid="avg-daily-net"
                    className={`text-3xl font-bold mt-1 ${
                      summary.avg_daily_net < 0
                        ? "text-red-600"
                        : "text-emerald-700"
                    }`}
                  >
                    {money(summary.avg_daily_net)}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t("finance.revenue.avgDailyHint", { count: summary.days })}
                  </p>
                </>
              )}
            </div>
            {summary.avg_net_per_trading_day !== null && (
              <p
                data-testid="avg-trading-day"
                className="text-sm text-gray-600 sm:text-end"
              >
                {t("finance.revenue.avgPerTradingDay", {
                  amount: money(summary.avg_net_per_trading_day),
                  count: summary.trading_days,
                })}
              </p>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Figure
                label={t("finance.revenue.revenue")}
                value={money(summary.revenue)}
                hint={t("common.sales", { count: summary.sales_count })}
              />
              <Figure
                label={t("finance.revenue.costOfGoods")}
                value={`− ${money(summary.cost_of_goods)}`}
                tone="muted"
              />
              <Figure
                label={t("finance.revenue.grossProfit")}
                value={money(summary.gross_profit)}
                tone="good"
              />
              <Figure
                label={t("finance.revenue.expenses")}
                value={`− ${money(summary.expenses)}`}
                tone="muted"
              />
              <Figure
                label={t("finance.revenue.netRevenue")}
                value={money(summary.net_revenue)}
                tone={summary.net_revenue < 0 ? "bad" : "good"}
                hint={t("finance.revenue.ofRevenue", {
                  percent: percent(summary.net_margin),
                })}
              />
            </div>

            {summary.expenses_by_category.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {t("finance.revenue.whereExpenses")}
                </p>
                <div className="flex flex-wrap gap-2">
                  {summary.expenses_by_category.map((row) => (
                    <span
                      key={row.category}
                      className="text-sm px-3 py-1.5 rounded-full bg-gray-100 text-gray-700"
                    >
                      {t(`finance.expenses.categories.${row.category}`)}{" "}
                      <span className="font-semibold whitespace-nowrap">
                        {money(row.total)}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              {t("finance.revenue.sixMonths")}
            </h2>
            <div className="space-y-2">
              {months.map((month) => (
                <div key={month.month} className="flex items-center gap-3">
                  <span className="w-20 text-sm text-gray-600 shrink-0">
                    {monthLabel(month.month, locale)}
                  </span>
                  <div className="flex-1 h-6 bg-gray-100 rounded-md overflow-hidden">
                    <div
                      className={`h-full ${
                        month.net_revenue < 0 ? "bg-red-400" : "bg-emerald-500"
                      }`}
                      style={{
                        width: `${(Math.abs(month.net_revenue) / peak) * 100}%`,
                      }}
                    />
                  </div>
                  <span
                    className={`w-28 sm:w-32 text-end text-sm font-semibold shrink-0 ${
                      month.net_revenue < 0 ? "text-red-600" : "text-gray-900"
                    }`}
                  >
                    {money(month.net_revenue)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="min-w-0 bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">
                {t("finance.revenue.dayByDay")}
              </h2>
              {journal.length === 0 ? (
                <p className="text-gray-500 py-6 text-center">
                  {t("finance.revenue.nothingPeriod")}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-start text-gray-500 border-b border-gray-200">
                        <th className="py-2 pe-3 text-start">
                          {t("finance.revenue.columns.day")}
                        </th>
                        <th className="py-2 pe-3 text-end">
                          {t("finance.revenue.columns.sold")}
                        </th>
                        <th className="py-2 pe-3 text-end">
                          {t("finance.revenue.columns.cost")}
                        </th>
                        <th className="py-2 pe-3 text-end">
                          {t("finance.revenue.columns.spent")}
                        </th>
                        <th className="py-2 text-end">
                          {t("finance.revenue.columns.kept")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {journal.map((day) => (
                        <tr key={day.date} className="border-b border-gray-50">
                          <td className="py-2 pe-3 whitespace-nowrap">
                            {formatDate(day.date)}
                          </td>
                          <td className="py-2 pe-3 text-end whitespace-nowrap">
                            {money(day.revenue)}
                          </td>
                          <td className="py-2 pe-3 text-end text-gray-500 whitespace-nowrap">
                            {money(day.cost_of_goods)}
                          </td>
                          <td className="py-2 pe-3 text-end text-gray-500 whitespace-nowrap">
                            {money(day.expenses)}
                          </td>
                          <td
                            className={`py-2 text-end font-semibold whitespace-nowrap ${
                              day.net_revenue < 0
                                ? "text-red-600"
                                : "text-emerald-700"
                            }`}
                          >
                            {money(day.net_revenue)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="min-w-0 bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-1">
                {t("finance.revenue.earnsMost")}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {t("finance.revenue.earnsMostHint")}
              </p>
              {margins.length === 0 ? (
                <p className="text-gray-500 py-6 text-center">
                  {t("finance.revenue.nothingSold")}
                </p>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {margins.slice(0, 12).map((row) => (
                    <li
                      key={row.id}
                      className="py-2.5 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">
                          {row.name}
                          {row.is_house_recipe && (
                            <span className="ms-2 text-xs px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">
                              {t("finance.revenue.ownRecipe")}
                            </span>
                          )}
                        </p>
                        <p className="text-xs text-gray-500">
                          {t("finance.revenue.unitsSold", {
                            count: row.units_sold,
                            amount: money(row.revenue),
                          })}
                          {row.has_uncosted_sales &&
                            t("finance.revenue.uncosted")}
                        </p>
                      </div>
                      <div className="text-end shrink-0">
                        <p className="font-semibold text-emerald-700 whitespace-nowrap">
                          {money(row.profit)}
                        </p>
                        <p className="text-xs text-gray-500">
                          {percent(row.margin)}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Revenue;
